import { useState, useEffect, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// F-1 issue #18: pdfjs worker を self-host (CDN .mjs が iOS Safari で起動失敗するため)
// scripts/copy-pdfjs-worker.js が build 時に node_modules → public/pdfjs/ にコピーする
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

// TODO: REMOVE AFTER #18 DEBUG — production iPhone Safari PDF 描画失敗の真因切り分け計装。
// catch 経路で localStorage + 画面赤 overlay にエラーを記録する。 ユーザーがスクショで送れるように。
// issue #52: H-1 (Blob backing 喪失) 修正後に再発した場合の H-2 (zero-byte) / H-3 (pdfjs)
// 切り分け用に pdfBlob.size + type を payload に追加。 同じく #18 close で撤去予定。
export function recordPdfDebugError(err: unknown, pdfBlob?: Blob | null): void {
  try {
    const e = err as { name?: string; message?: string } | undefined;
    const name = e?.name ?? (typeof err === 'string' ? 'StringThrown' : 'UnknownError');
    const message = e?.message ?? (typeof err === 'string' ? err : 'no message');
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : 'no-ua';
    const ts = new Date().toISOString();
    const sizeInfo = pdfBlob
      ? `(size=${pdfBlob.size},type=${pdfBlob.type})`
      : '(no-blob)';
    const payload = `[pdf-debug-#18] ${ts} ${name}: ${message} ${sizeInfo} | UA: ${ua}`;
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('omakase-unshi:debug:pdf-#18', payload);
    }
    if (typeof document !== 'undefined' && document.body) {
      const existing = document.getElementById('pdf-debug-18-overlay');
      if (existing) existing.remove();
      const overlay = document.createElement('div');
      overlay.id = 'pdf-debug-18-overlay';
      overlay.setAttribute('data-testid', 'pdf-debug-18-overlay');
      overlay.style.cssText = 'position:fixed;top:60px;left:8px;right:8px;padding:8px;background:rgba(255,200,200,0.95);color:#111;font:11px monospace;z-index:9999;border:2px solid #c00;border-radius:4px;word-break:break-all;';
      overlay.textContent = payload;
      document.body.appendChild(overlay);
    }
  } catch {
    // overlay 生成失敗で本体クラッシュさせない
  }
}

function openPdf(data: ArrayBuffer) {
  return pdfjsLib.getDocument({ data }).promise;
}

export function usePdfBackground(
  pdfBlob: Blob | null,
  pageNumber: number = 1,
  scale: number = 2,
): { imageUrl: string | null; pageCount: number } {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState(1);
  const prevUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pdfBlob) {
      setImageUrl(null);
      setPageCount(1);
      return;
    }

    let cancelled = false;
    let pdfDoc: pdfjsLib.PDFDocumentProxy | null = null;

    (async () => {
      const arrayBuffer = await pdfBlob.arrayBuffer();
      pdfDoc = await openPdf(arrayBuffer);
      if (cancelled) { pdfDoc.destroy(); return; }

      setPageCount(pdfDoc.numPages);

      const page = await pdfDoc.getPage(pageNumber);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { pdfDoc.destroy(); return; }

      await page.render({ canvas, canvasContext: ctx, viewport }).promise;

      if (cancelled) { canvas.width = 0; canvas.height = 0; pdfDoc.destroy(); return; }

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob ? canvas.toBlob(resolve, 'image/png') : resolve(null),
      );
      canvas.width = 0;
      canvas.height = 0;

      if (!cancelled && blob) {
        if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
        const url = URL.createObjectURL(blob);
        prevUrlRef.current = url;
        setImageUrl(url);
      }

      pdfDoc.destroy();
      pdfDoc = null;
    })().catch((err) => {
      // issue #82: ページ遷移による正常キャンセルは握りつぶす (赤 overlay 誤表示を防ぐ)。
      // instanceof を主判定、 name 判定をフォールバック (jsdom mock / 実機差異の両対応)。
      if (
        err instanceof pdfjsLib.RenderingCancelledException ||
        (err as { name?: string } | undefined)?.name === 'RenderingCancelledException'
      ) {
        return;
      }
      if (!cancelled) setImageUrl(null);
      // TODO: REMOVE AFTER #18 DEBUG
      // issue #52: pdfBlob を渡して size/type を payload に含める (H-2/H-3 切り分け)
      recordPdfDebugError(err, pdfBlob);
    });

    return () => {
      cancelled = true;
      pdfDoc?.destroy();
      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = null;
      }
    };
  }, [pdfBlob, pageNumber, scale]);

  return { imageUrl, pageCount };
}
